
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Sparkles, ChevronDown, Zap } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeSwitcher } from "../ThemeSwitcher";
import { UserProfileDropdown } from "../auth/UserProfileDropdown";
import { cn } from "@/lib/utils";

const navigation = [
  {
    name: "Discover Events",
    href: "/discover",
    subItems: [
      { name: "All Events", href: "/discover", description: "Browse all available events" },
      { name: "Popular Events", href: "/discover?featured=true", description: "Most attended events" }
    ]
  },
  {
    name: "Templates",
    href: "/templates",
    badge: "New"
  },
  {
    name: "Pricing",
    href: "/pricing"
  },
];

export function LandingHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    if (isMenuOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [navigate]);

  if (!mounted) return <div className="h-16" />;

  return (
    <>
      <header className={cn(
        "fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300",
        scrolled ? "border-b bg-background shadow-md" : "border-b border-border/40 bg-background/95 backdrop-blur-sm"
      )}>
        <nav className={cn(
          "container flex items-center justify-between px-4 transition-all duration-300",
          scrolled ? "h-14" : "h-16"
        )}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-2 group relative z-10"
            aria-label="AboutBlank home"
          >
            <div className="relative">
              <Sparkles className="h-6 w-6 text-theme-primary transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
              <span className="absolute inset-0 h-6 w-6 bg-theme-primary/20 blur-xl transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-theme-primary via-theme-primary to-theme-secondary bg-clip-text text-transparent">
              AboutBlank
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-1">
            {navigation.map((item) =>
              item.subItems ? (
                <DropdownMenu key={item.name}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="text-sm font-medium text-foreground/70 hover:text-theme-primary hover:bg-theme-primary/10 transition-colors h-9 px-3"
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56 bg-background border shadow-lg">
                    {item.subItems.map((subItem) => (
                      <DropdownMenuItem
                        key={subItem.href}
                        onClick={() => handleNavigate(subItem.href)}
                        className="cursor-pointer focus:bg-theme-primary/10 hover:bg-theme-primary/10 hover:text-theme-primary focus:text-theme-primary"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">{subItem.name}</span>
                          {subItem.description && (
                            <span className="text-xs text-muted-foreground">{subItem.description}</span>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "relative text-sm font-medium transition-colors h-9 px-3 flex items-center rounded-md hover:bg-theme-primary/10 hover:text-theme-primary",
                    location.pathname === item.href
                      ? "text-theme-primary font-semibold"
                      : "text-foreground/70"
                  )}
                >
                  {item.name}
                  {item.badge && (
                    <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-theme-primary to-theme-secondary text-white rounded shadow-sm">
                      {item.badge}
                    </span>
                  )}
                  {location.pathname === item.href && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-gradient-to-r from-theme-primary to-theme-secondary rounded-full" />
                  )}
                </Link>
              )
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-2">
            <ThemeSwitcher />
            {isAuthenticated ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className="hidden lg:inline-flex h-9 text-foreground/70 hover:text-theme-primary hover:bg-theme-primary/10"
                >
                  <Zap className="mr-2 h-4 w-4" />
                  Dashboard
                </Button>
                <UserProfileDropdown />
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/signin')}
                  className="hidden md:inline-flex h-9 text-foreground/70 hover:text-theme-primary hover:bg-theme-primary/10"
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="hidden md:inline-flex h-9 bg-gradient-to-r from-theme-primary to-theme-secondary hover:from-theme-primary-hover hover:to-theme-secondary-hover text-white shadow-md"
                >
                  Get Started
                </Button>
              </>
            )}
            {/* Hamburger */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-9 w-9 hover:bg-theme-primary/10"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <div
          id="mobile-menu"
          className={cn(
            "md:hidden border-t bg-background transition-all duration-300 ease-in-out overflow-hidden",
            isMenuOpen
              ? "max-h-[80vh] opacity-100 shadow-lg"
              : "max-h-0 opacity-0 pointer-events-none"
          )}
          role="menu"
          aria-hidden={!isMenuOpen}
        >
          <div className="container px-4 py-4 space-y-1 overflow-y-auto max-h-[70vh]">
            {navigation.map((item) => (
              <div key={item.name}>
                {item.subItems ? (
                  <>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-foreground/70 hover:text-theme-primary hover:bg-theme-primary/10 rounded-md transition-colors"
                    >
                      <span>{item.name}</span>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 transition-transform duration-200",
                          activeDropdown === item.name && "rotate-180"
                        )}
                      />
                    </button>
                    <div
                      className={cn(
                        "pl-4 space-y-1 overflow-hidden transition-all duration-200",
                        activeDropdown === item.name ? "max-h-48 mt-1" : "max-h-0"
                      )}
                    >
                      {item.subItems.map((subItem) => (
                        <button
                          key={subItem.href}
                          onClick={() => handleNavigate(subItem.href)}
                          className="w-full text-left px-3 py-2 text-sm text-foreground/70 hover:text-theme-primary hover:bg-theme-primary/10 rounded-md transition-colors"
                        >
                          {subItem.name}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                      location.pathname === item.href
                        ? "text-theme-primary bg-theme-primary/10 font-semibold"
                        : "text-foreground/70 hover:text-theme-primary hover:bg-theme-primary/10"
                    )}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                    {item.badge && (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-theme-primary to-theme-secondary text-white rounded shadow-sm">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            ))}
            {/* Mobile Auth */}
            <div className="pt-4 space-y-2 border-t mt-4">
              {isAuthenticated ? (
                <Button
                  onClick={() => handleNavigate('/dashboard')}
                  className="w-full justify-start h-10 bg-gradient-to-r from-theme-primary to-theme-secondary hover:from-theme-primary-hover hover:to-theme-secondary-hover text-white shadow-md"
                >
                  <Zap className="mr-2 h-4 w-4" />
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => handleNavigate('/signin')}
                    className="w-full justify-start h-10 hover:bg-theme-primary/10 hover:text-theme-primary"
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => handleNavigate('/signup')}
                    className="w-full justify-start h-10 bg-gradient-to-r from-theme-primary to-theme-secondary hover:from-theme-primary-hover hover:to-theme-secondary-hover text-white shadow-md"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className={cn("transition-all duration-300", scrolled ? "h-14" : "h-16")} />
    </>
  );
}
